import React from "react";
import Form from "react-bootstrap/Form";
import * as yup from "yup";
import { Formik } from "formik";
import {
  ValidateGroupEmail,
  GroupExistsWithNameAPI,
  CreateGroupAsyncAPI,
  EditGroupAsyncAPI,
} from "../../../../services/api/group-api";
import "./admin-group-create-panel.scss";
import AdminGroupFormBottomColumnNew from "./admin-group-form-bottom-column-new";
import PropTypes from "prop-types";
import AdminGroupFormLeftColumnNew from "./admin-group-form-left-column-new";
import AdminGroupFormRightColumnNew from "./admin-group-form-right-column-new";
import { GroupNameRegex } from "../../../../util/validity-helpers";

const AdminGroupFormNew = ({
  group,
  setGroup,
  isView,
  isEdit,
  setPanelVisibility,
  setAlertConfig,
  isSuperAdmin,
  departments,
}) => {
  const SaveGroup = async (formGroup, actions) => {
    setAlertConfig((prev) => ({
      ...prev,
      showAlert: false,
    }));
    
    if (!HasMembers(actions)) return;

    let result = await GroupExistsWithNameAPI(formGroup.Name);

    if (result.data) {
      if (DoesGroupExist(result, formGroup, actions)) return;
      else UpdateGroup(formGroup, actions);
    } else UpdateGroup(formGroup, actions);
  };

  const HasMembers = (actions) => {
    if (group.data.Members.length === 0) {
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: isEdit ? "Cannot Edit Group" : "Cannot Create Group",
        alertMessage: "Group must have at least 1 member.",
        alertVariant: "danger",
      }));
      actions.setSubmitting(false);
      return false;
    }
    return true;
  };

  const DoesGroupExist = (result, formGroup, actions) => {
    if (result.data.Id !== formGroup.Id) {
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: isEdit ? "Cannot Update Group" : "Cannot Create Group",
        alertMessage: "A group with that name exists.",
        alertVariant: "danger",
      }));
      actions.setSubmitting(false);
      return true;
    }
    return false;
  };

  const UpdateGroup = async (formGroup, actions) => {
    let result = await IsValidEmail(formGroup);

    if (result?.status === 200) {
      let groupResponse = {
        ...formGroup,
        Members: group.data.Members,
      };
      try {
        isEdit
          ? await EditGroupAsyncAPI(groupResponse)
          : await CreateGroupAsyncAPI(groupResponse);
        setPanelVisibility((prev) => ({
          ...prev,
          showConfirmPanel: true,
        }));
      } catch (error) {
        console.log(error);
        setAlertConfig((prev) => ({
          ...prev,
          showAlert: true,
          alertAction: isEdit ? "Cannot Edit Group" : "Cannot Create Group",
          alertMessage: error?.toString(),
          alertVariant: "danger",
        }));
      } finally {
        actions.setSubmitting(false);
      }
    } else {
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: isEdit ? "Cannot Update Group" : "Cannot Create Group",
        alertMessage: result.response?.data,
        alertVariant: "danger",
      }));
      actions.setSubmitting(false);
    }
  };

  const IsValidEmail = async (formGroup) => {
    try {
      let result = await ValidateGroupEmail(formGroup.Email, formGroup.Id);
      return result;
    } catch (error) {
      console.log("API [ValidateGroupEmail] error ->");
      console.log(error);
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: isEdit ? "Cannot Update Group" : "Cannot Create Group",
        alertMessage: error?.response?.data,
        alertVariant: "danger",
      }));
    }
  };

  const OnKeyDown = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  return (
    <Formik
      key={group?.IsLoading}
      validationSchema={yup.object({
        Name: yup
          .string()
          .required("Group Name Required")
          .test(
            "length",
            "The field Group Name must be a string with a minimum length of 2 and a maximum length of 30 characters.",
            (val) => val && val.length > 1 && val.length < 31
          )
          .matches(
            GroupNameRegex,
            "Please do not enter any special characters in group name"
          ),
        Email: yup.string().email("Invalid email address").nullable(),
        Description: yup
          .string()
          .max(3000, "Description must be under 3000 characters"),
        Active: yup.boolean().required(),
      })}
      onSubmit={(values, actions) => {
        SaveGroup(values, actions);
      }}
      initialValues={group?.data}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        setFieldValue,
        isSubmitting,
        submitForm,
        isValid,
      }) => (
        <Form
          className="group-creation-container"
          onSubmit={handleSubmit}
          onKeyDown={OnKeyDown}
        >
          <AdminGroupFormLeftColumnNew
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            touched={touched}
            errors={errors}
            isView={isView}
            isEdit={isEdit}
            setGroup={setGroup}
            group={group}
          />
          <AdminGroupFormRightColumnNew
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            touched={touched}
            errors={errors}
            isView={isView}
            setFieldValue={setFieldValue}
            isEdit={isEdit}
            isSuperAdmin={isSuperAdmin}
            departments={departments}
          />
          <AdminGroupFormBottomColumnNew
            isView={isView}
            isEdit={isEdit}
            isSubmitting={isSubmitting}
            isValid={isValid}
            setPanelVisibility={setPanelVisibility}
            submitForm={submitForm}
          />
        </Form>
      )}
    </Formik>
  );
};

AdminGroupFormNew.propTypes = {
  group: PropTypes.object.isRequired,
  setAlertConfig: PropTypes.func.isRequired,
  setPanelVisibility: PropTypes.func.isRequired,
  setGroup: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isView: PropTypes.bool.isRequired,
  isSuperAdmin: PropTypes.bool.isRequired,
  departments: PropTypes.array.isRequired,
};

export default AdminGroupFormNew;
